import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  sendPasswordResetEmail,
  sendEmailVerification,
  updateProfile,
  updateEmail,
  updatePassword,
  confirmPasswordReset,
  verifyPasswordResetCode,
  applyActionCode,
} from 'firebase/auth';
import { auth } from './firebase';
import { createUserDocument, getUserDocument } from './userService';

const googleProvider = new GoogleAuthProvider();

/**
 * Sign up with email and password
 * @param {string} email
 * @param {string} password
 * @param {Object} userData - Additional user data (displayName, userType, organizationId, etc.)
 * @returns {Promise<Object>} - User credential with user data
 */
export const signUpWithEmail = async (email, password, userData) => {
  try {
    console.log('🔵 authService: Creating Firebase Auth user...');
    // Create Firebase Auth user
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    console.log('✅ authService: Firebase Auth user created:', user.uid);

    // Update display name in Firebase Auth
    if (userData.displayName) {
      console.log('🔵 authService: Updating display name...');
      await updateProfile(user, {
        displayName: userData.displayName,
      });
      console.log('✅ authService: Display name updated');
    }

    // Send email verification
    console.log('🔵 authService: Sending email verification...');
    try {
      await sendEmailVerification(user);
      console.log('✅ authService: Email verification sent to', user.email);
    } catch (error) {
      console.error('⚠️ authService: Failed to send verification email:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      // Don't throw - allow registration to continue even if email fails
      // But we should notify the user
    }

    console.log('🔵 authService: Creating Firestore user document...');
    // Create Firestore user document
    await createUserDocument(user.uid, {
      email: user.email,
      displayName: userData.displayName,
      userType: userData.userType,
      organizationId: userData.organizationId,
      organizationName: userData.organizationName,
      phoneNumber: userData.phoneNumber || '',
      photoURL: user.photoURL || '',
      skills: userData.skills || [],
      availability: userData.availability || {},
      status: userData.status || 'active',
    });
    console.log('✅ authService: Firestore user document created');

    console.log('🔵 authService: Fetching user document...');
    // Get the created user document
    const userDoc = await getUserDocument(user.uid);
    console.log('✅ authService: User document fetched');

    return {
      user,
      userData: userDoc,
    };
  } catch (error) {
    console.error('❌ authService error:', error);
    throw new Error(getAuthErrorMessage(error.code));
  }
};

/**
 * Sign in with email and password
 * @param {string} email
 * @param {string} password
 * @returns {Promise<Object>} - User credential with user data
 */
export const signInWithEmail = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // Get user document from Firestore
    const userDoc = await getUserDocument(user.uid);

    if (!userDoc) {
      throw new Error('User profile not found. Please contact support.');
    }

    return {
      user,
      userData: userDoc,
    };
  } catch (error) {
    console.error('Error signing in with email:', error);
    throw new Error(getAuthErrorMessage(error.code));
  }
};

/**
 * Sign in with Google
 * @returns {Promise<Object>} - User credential with user data
 */
export const signInWithGoogle = async () => {
  try {
    const userCredential = await signInWithPopup(auth, googleProvider);
    const user = userCredential.user;

    // Check if user document exists
    let userDoc = await getUserDocument(user.uid);

    // If user doesn't exist in Firestore, return user info for registration flow
    if (!userDoc) {
      return {
        user,
        userData: null,
        isNewUser: true,
      };
    }

    return {
      user,
      userData: userDoc,
      isNewUser: false,
    };
  } catch (error) {
    console.error('Error signing in with Google:', error);
    throw new Error(getAuthErrorMessage(error.code));
  }
};

/**
 * Complete Google sign-up (called after organization selection)
 * @param {Object} user - Firebase Auth user
 * @param {Object} userData - User data including organizationId
 * @returns {Promise<Object>} - User data
 */
export const completeGoogleSignUp = async (user, userData) => {
  try {
    // Create Firestore user document
    await createUserDocument(user.uid, {
      email: user.email,
      displayName: user.displayName || userData.displayName,
      userType: userData.userType,
      organizationId: userData.organizationId,
      organizationName: userData.organizationName,
      phoneNumber: userData.phoneNumber || '',
      photoURL: user.photoURL || '',
      skills: userData.skills || [],
      availability: userData.availability || {},
      status: userData.status || 'active',
    });

    // Get the created user document
    const userDoc = await getUserDocument(user.uid);

    return userDoc;
  } catch (error) {
    console.error('Error completing Google sign-up:', error);
    throw new Error('Failed to complete registration: ' + error.message);
  }
};

/**
 * Sign out
 * @returns {Promise<void>}
 */
export const logOut = async () => {
  try {
    await signOut(auth);
  } catch (error) {
    console.error('Error signing out:', error);
    throw new Error('Failed to sign out: ' + error.message);
  }
};

/**
 * Send password reset email
 * @param {string} email
 * @returns {Promise<void>}
 */
export const resetPassword = async (email) => {
  try {
    await sendPasswordResetEmail(auth, email);
  } catch (error) {
    console.error('Error sending password reset email:', error);
    throw new Error(getAuthErrorMessage(error.code));
  }
};

/**
 * Verify a password reset code from the email link
 * @param {string} oobCode
 * @returns {Promise<string>} Email address associated with the code
 */
export const verifyResetCode = async (oobCode) => {
  try {
    return await verifyPasswordResetCode(auth, oobCode);
  } catch (error) {
    console.error('Error verifying reset code:', error);
    throw new Error('This password reset link is invalid or has expired. Please request a new one.');
  }
};

/**
 * Complete a password reset using the oobCode from the email link
 * @param {string} oobCode
 * @param {string} newPassword
 * @returns {Promise<void>}
 */
export const completePasswordReset = async (oobCode, newPassword) => {
  try {
    await confirmPasswordReset(auth, oobCode, newPassword);
  } catch (error) {
    console.error('Error completing password reset:', error);
    throw new Error(getAuthErrorMessage(error.code));
  }
};

/**
 * Apply an email action code (verify email, recover email)
 * @param {string} oobCode
 * @returns {Promise<void>}
 */
export const applyEmailActionCode = async (oobCode) => {
  try {
    await applyActionCode(auth, oobCode);
  } catch (error) {
    console.error('Error applying action code:', error);
    throw new Error('This link is invalid or has expired. Please try again.');
  }
};

/**
 * Update user profile
 * @param {Object} updates - Profile updates (displayName, photoURL)
 * @returns {Promise<void>}
 */
export const updateUserProfile = async (updates) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('No user is currently signed in');
    }

    // Update Firebase Auth profile
    if (updates.displayName || updates.photoURL) {
      await updateProfile(user, {
        ...(updates.displayName && { displayName: updates.displayName }),
        ...(updates.photoURL && { photoURL: updates.photoURL }),
      });
    }

    // Update Firestore user document is handled separately in userService
  } catch (error) {
    console.error('Error updating user profile:', error);
    throw new Error('Failed to update profile: ' + error.message);
  }
};

/**
 * Update user email
 * @param {string} newEmail
 * @returns {Promise<void>}
 */
export const updateUserEmail = async (newEmail) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('No user is currently signed in');
    }

    await updateEmail(user, newEmail);
  } catch (error) {
    console.error('Error updating email:', error);
    throw new Error(getAuthErrorMessage(error.code));
  }
};

/**
 * Update user password
 * @param {string} newPassword
 * @returns {Promise<void>}
 */
export const updateUserPassword = async (newPassword) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('No user is currently signed in');
    }

    await updatePassword(user, newPassword);
  } catch (error) {
    console.error('Error updating password:', error);
    throw new Error(getAuthErrorMessage(error.code));
  }
};

/**
 * Get current user
 * @returns {Object|null} - Current Firebase Auth user
 */
export const getCurrentUser = () => {
  return auth.currentUser;
};

/**
 * Send email verification to current user
 * @returns {Promise<void>}
 */
export const sendVerificationEmail = async () => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('No user is currently signed in');
    }

    if (user.emailVerified) {
      throw new Error('Email is already verified');
    }

    await sendEmailVerification(user);
  } catch (error) {
    console.error('Error sending verification email:', error);
    throw new Error('Failed to send verification email: ' + error.message);
  }
};

/**
 * Reload current user to get updated emailVerified status
 * @returns {Promise<void>}
 */
export const reloadUser = async () => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('No user is currently signed in');
    }

    await user.reload();
  } catch (error) {
    console.error('Error reloading user:', error);
    throw new Error('Failed to reload user: ' + error.message);
  }
};

/**
 * Convert Firebase Auth error codes to user-friendly messages
 * @param {string} errorCode
 * @returns {string} - User-friendly error message
 */
const getAuthErrorMessage = (errorCode) => {
  switch (errorCode) {
    case 'auth/email-already-in-use':
      return 'This email is already registered. Please sign in instead.';
    case 'auth/invalid-email':
      return 'Invalid email address.';
    case 'auth/operation-not-allowed':
      return 'Operation not allowed. Please contact support.';
    case 'auth/weak-password':
      return 'Password is too weak. Please use at least 6 characters.';
    case 'auth/user-disabled':
      return 'This account has been disabled. Please contact support.';
    case 'auth/user-not-found':
      return 'No account found with this email address.';
    case 'auth/wrong-password':
      return 'Incorrect password. Please try again.';
    case 'auth/invalid-credential':
      return 'No account found with this email address, or the password is incorrect.';
    case 'auth/too-many-requests':
      return 'Too many failed attempts. Please try again later.';
    case 'auth/popup-closed-by-user':
      return 'Sign-in popup was closed. Please try again.';
    case 'auth/cancelled-popup-request':
      return 'Sign-in cancelled. Please try again.';
    case 'auth/requires-recent-login':
      return 'Please sign in again to complete this action.';
    default:
      return 'An error occurred. Please try again.';
  }
};

export default {
  signUpWithEmail,
  signInWithEmail,
  signInWithGoogle,
  completeGoogleSignUp,
  logOut,
  resetPassword,
  updateUserProfile,
  updateUserEmail,
  updateUserPassword,
  getCurrentUser,
  sendVerificationEmail,
  reloadUser,
};
